import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { ApiError } from '../services/api.error';
import { loginWithGoogle } from '../services/auth';
import {
  isGoogleSignInConfigured,
  isNativeGoogleSignIn,
  renderGoogleButton,
  requestNativeGoogleIdToken,
} from '../services/google-auth';
import { fetchActiveLegalDocument, invalidateLegalDocumentCache } from '../services/legalDocuments';
import { useTranslations } from '../translations';
import GoogleTermsConsentModal from './GoogleTermsConsentModal';
import './GoogleSignInButton.css';

interface GoogleSignInButtonProps {
  onSuccess: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  /** Google's own button copy. Register pages read better with `signup_with`. */
  variant?: 'continue_with' | 'signin_with' | 'signup_with';
}

/** Google rejects a `width` outside 200–400px and then renders nothing at all. */
const googleButtonWidth = (available: number): number | undefined => (
  available ? Math.min(Math.max(available, 200), 400) : undefined
);

/**
 * Google sign-in entry point.
 *
 * On the web Google renders its own button; on native the platform plugin is driven by
 * a button of ours, because Google Identity Services is a browser SDK.
 *
 * A brand-new account needs an explicit terms acceptance before it can be created, so a
 * `google_terms_acceptance_required` answer opens a consent step and retries. Users who
 * already have an account never see it.
 */
const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onSuccess,
  onNavigateToTerms,
  onNavigateToPrivacy,
  variant = 'continue_with',
}) => {
  const t = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const pendingIdTokenRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [consentRequired, setConsentRequired] = useState(false);

  const configured = isGoogleSignInConfigured();
  const native = isNativeGoogleSignIn();
  const label = variant === 'signup_with'
    ? t.googleSignIn.signUpWithGoogle
    : t.googleSignIn.continueWithGoogle;
  const submittingLabel = variant === 'signup_with'
    ? t.googleSignIn.signingUp
    : t.googleSignIn.signingIn;

  const submit = useCallback(async (
    idToken: string,
    consent?: { termsVersion: string; marketingEmails: boolean },
  ) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await loginWithGoogle({
        idToken,
        termsVersion: consent?.termsVersion,
        marketingEmails: consent?.marketingEmails,
      });
      pendingIdTokenRef.current = null;
      setConsentRequired(false);
      onSuccess();
    } catch (failure) {
      if (failure instanceof ApiError && failure.hasErrorCode('google_terms_acceptance_required')) {
        pendingIdTokenRef.current = idToken;
        setConsentRequired(true);
      } else if (failure instanceof ApiError
        && failure.hasErrorCode('legal_document_version_not_active')) {
        // The active terms changed between loading the consent step and submitting it.
        invalidateLegalDocumentCache('TERMS');
        pendingIdTokenRef.current = idToken;
        setConsentRequired(true);
        setError(t.googleSignIn.termsUpdated);
      } else if (failure instanceof ApiError
        && (failure.status === 429 || failure.hasErrorCode('authentication_rate_limited'))) {
        setError(t.googleSignIn.rateLimited);
      } else if (failure instanceof ApiError && failure.hasErrorCode('google_email_not_verified')) {
        setError(t.googleSignIn.emailNotVerified);
      } else if (failure instanceof ApiError && failure.hasErrorCode('google_identity_conflict')) {
        setError(t.googleSignIn.identityConflict);
      } else if (failure instanceof ApiError && failure.hasErrorCode('google_account_link_required')) {
        setError(t.googleSignIn.accountLinkRequired);
      } else if (failure instanceof ApiError && failure.status === 403) {
        setError(t.googleSignIn.accountBlocked);
      } else {
        setError(t.googleSignIn.error);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [onSuccess, t.googleSignIn]);

  useEffect(() => {
    if (!configured || native) {
      return;
    }
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let cleanup: (() => void) | undefined;
    let cancelled = false;
    let rendered = false;
    let renderedWidth: number | undefined;
    let renderController: AbortController | undefined;

    /*
     * Google lays the button out at the width it was given and never reflows it, so a
     * container that changes width — rotation, a resized window, Chrome's device
     * toolbar — leaves the real click target at its old size behind our overlay. Each
     * new width gets a fresh button.
     */
    const render = () => {
      const width = googleButtonWidth(container.offsetWidth);
      if (rendered && width === renderedWidth) {
        return;
      }
      rendered = true;
      renderedWidth = width;
      renderController?.abort();
      const controller = new AbortController();
      renderController = controller;
      cleanup?.();
      cleanup = undefined;

      renderGoogleButton(
        container,
        idToken => { void submit(idToken); },
        () => setError(t.googleSignIn.error),
        { text: variant, width },
        controller.signal,
      )
        .then(dispose => {
          if (cancelled || controller.signal.aborted) {
            dispose();
            return;
          }
          cleanup = dispose;
        })
        .catch(() => {
          if (!cancelled) {
            setUnavailable(true);
          }
        });
    };

    render();
    const observer = new ResizeObserver(render);
    observer.observe(container);

    return () => {
      cancelled = true;
      renderController?.abort();
      observer.disconnect();
      cleanup?.();
    };
  }, [configured, native, submit, t.googleSignIn, variant]);

  const handleNativeSignIn = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const idToken = await requestNativeGoogleIdToken();
      await submit(idToken);
    } catch {
      setError(t.googleSignIn.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsent = async (marketingEmails: boolean) => {
    const idToken = pendingIdTokenRef.current;
    if (!idToken) {
      setConsentRequired(false);
      return;
    }
    setError(null);
    try {
      const terms = await fetchActiveLegalDocument('TERMS');
      await submit(idToken, { termsVersion: terms.documentVersion, marketingEmails });
    } catch {
      setError(t.googleSignIn.termsLoadError);
    }
  };

  const handleConsentCancel = () => {
    pendingIdTokenRef.current = null;
    setConsentRequired(false);
    setError(null);
  };

  if (!configured || unavailable) {
    return null;
  }

  const loadingContent = (
    <>
      <LoaderCircle className="google-sign-in-button-spinner" aria-hidden="true" />
      <span>{submittingLabel}</span>
    </>
  );

  return (
    <div className="google-sign-in">
      <div className="google-sign-in-separator" role="presentation">
        <span>{t.googleSignIn.separator}</span>
      </div>

      {native ? (
        <button
          type="button"
          className="google-sign-in-button"
          onClick={() => void handleNativeSignIn()}
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? loadingContent : (
            <>
              <GoogleMark />
              <span>{label}</span>
            </>
          )}
        </button>
      ) : (
        <div
          className="google-sign-in-web"
          data-submitting={isSubmitting ? 'true' : undefined}
          aria-busy={isSubmitting}
        >
          <div
            className="google-sign-in-button google-sign-in-button-visual"
            aria-hidden="true"
          >
            <GoogleMark />
            <span>{label}</span>
          </div>
          <div
            ref={containerRef}
            className="google-sign-in-button-host"
          />
          {isSubmitting && (
            <div className="google-sign-in-button google-sign-in-button-loading" role="status" aria-live="polite">
              {loadingContent}
            </div>
          )}
        </div>
      )}

      {error && <p className="google-sign-in-error" role="alert">{error}</p>}

      {consentRequired && (
        <GoogleTermsConsentModal
          onConfirm={handleConsent}
          onCancel={handleConsentCancel}
          onNavigateToTerms={onNavigateToTerms}
          onNavigateToPrivacy={onNavigateToPrivacy}
        />
      )}
    </div>
  );
};

const GoogleMark: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.34A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.09l3.01-2.34z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.34C4.68 5.17 6.66 3.58 9 3.58z" />
  </svg>
);

export default GoogleSignInButton;
